import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';

import { Observable, of } from 'rxjs';
import { tap, map, catchError, delay } from 'rxjs/operators';

import { BaseMiscApiService } from '@common/api/base-misc-api.service';
import { MatomoTrackerService } from '@common/services/matomo-tracker.service';
import { ImpersonateService } from '@apps/root/services/impersonate.service';

import { TokenService } from './token.service';
import { Profile } from '../models/profile';
import { LoginResult } from '../models/loginResult';
import { ProfileApi } from './models/profile-api';
import { PasswordResetData } from './models/reset-pswd';
import { ChangePassword } from './models';
import { AppConstants } from '../../app.constants';
import { UtilsService } from '@common/services';
import { MfaSetup } from './models/mfa-setup';
import { IdentityResult } from '../models/identity-result';

@Injectable()
export class AuthenticationService extends BaseMiscApiService {
    constructor(
        http: HttpClient,
        private tokenService: TokenService,
        private impersonateService: ImpersonateService,
        private matomoTrackerService: MatomoTrackerService
    ) {
        super(http);

        this.segmentRoot = AppConstants.Configuration.apiUrlAuth;
        this.segmentApi = '/auth/accounts';
    }

    login(email: string, password: string): Observable<LoginResult> {
        return this.post<LoginResult>('/login', { email: email, password: password })
            .pipe(
                map((loginResult) => {
                    // Normalisation défensive (évite undefined)
                    return {
                        ...loginResult,
                        twoFactorRequired: !!(loginResult as any)?.twoFactorRequired,
                        twoFactorMailRequired: !!(loginResult as any)?.twoFactorMailRequired,
                        twoFactorSetupRequired: !!(loginResult as any)?.twoFactorSetupRequired
                    } as LoginResult;
                }),
                tap((loginResult) => {
                    this.tokenService.saveToken(loginResult.tokens);
                }),
                catchError((err) => {
                    throw err;
                })
            );
    }

    profile(): Observable<Profile> {
        if (AppConstants.IsDataLocal('auth')) {
            return this.http.get('assets/data/auth/profile.json')
                .pipe(
                    map((obj: any): ProfileApi =>
                        obj.profile
                    ),
                    map(Profile.mapperFromApi)
                );
        }

        return this.get<LoginResult>('/profile')
            .pipe(
                map((obj: any): ProfileApi =>
                    obj.profile
                ),
                map(Profile.mapperFromApi)
            );
    }

    isProfileComplete(): Observable<boolean> {
        return this.get<boolean>('/complete');
    }

    updateProfile(profile: Profile): Observable<Profile> {
        return this.put<ProfileApi>('/profile', ProfileApi.mapperToApi(profile))
            .pipe(
                map(Profile.mapperFromApi)
            );
    }

    logout(): Observable<boolean> {
        if (!UtilsService.isNullOrEmpty(AppConstants.Configuration.trackingUrl) && this.matomoTrackerService) {
            this.matomoTrackerService.resetUserId();
            this.matomoTrackerService.appendToTrackingUrl('');
        }

        this.impersonateService.unsetImpersonation();

        return this.tokenService.logout();
    }

    // TODO: Quel type de donnée renvoyer
    resetPasswordSendLink(email: string): Observable<boolean> {
        return this.post('/pswd/reset/sendlink', { email: email })
            .pipe(
                map(() => true)
            );
    }

    resetPasswordVerifyToken(token: string): Observable<boolean> {
        return this.post('/pswd/reset/verifytoken', { token: token })
            .pipe(
                map(() => true)
            );
    }

    resetPassword(resetPasswordData: PasswordResetData): Observable<boolean> {
        return this.post<boolean>('/pswd/reset', resetPasswordData);
    }

    resetUserPassword(email: string): Observable<IdentityResult> {
        return this.post<IdentityResult>(`/pswd/reset/${email}`, {});
    }

    changePassword(changePassword: ChangePassword): Observable<Profile> {
        // return this.post<any>('/pswd/change', changePassword);

        return this.post<LoginResult>('/pswd/change', changePassword)
            .pipe(
                tap((loginResult) => {
                    this.tokenService.saveToken(loginResult.tokens);
                }),
                map((obj: any): ProfileApi =>
                    obj.profile
                ),
                map(Profile.mapperFromApi),
                catchError((err) => {
                    throw err;
                })
            );
    }

    isEmailTaken(email: string): Observable<boolean> {
        return of(true).pipe(delay(5000));
    }

    twoFactorSetup(): Observable<MfaSetup> {
        return this.post<MfaSetup>('/mfa/setup', {});
    }

    twoFactorSetupWithIdToken(idToken: string): Observable<MfaSetup> {
        return this.post<MfaSetup>('/mfa/setup', { idToken });
    }

    twoFactorVerifySetup(verifyCode: string): Observable<boolean> {
        return this.post<boolean>('/mfa/verify-setup', { verifyCode: verifyCode });
    }

    twoFactorVerifySetupWithIdToken(idToken: string, verifyCode: string): Observable<boolean> {
        return this.post<boolean>('/mfa/verify-setup', { idToken: idToken, verifyCode: verifyCode });
    }

    twoFactorDisable(): Observable<boolean> {
        return this.post<boolean>('/mfa/disable', {});
    }

    resetTwoFactor(): Observable<boolean> {
        return this.patch<boolean>('/mfa/reset', {});
    }

    resetTwoFactorById(userId: string): Observable<boolean> {
        return this.patch<boolean>(`/mfa/reset/${userId}`, {});
    }

    twoFactorVerify(idToken: string, verifyCode: string): Observable<LoginResult> {
        return this.post<LoginResult>('/mfa/verify', { idToken, verifyCode })
            .pipe(
                tap((loginResult) => {
                    this.tokenService.saveToken(loginResult.tokens);
                }),
                catchError((err) => {
                    throw err;
                })
            );
    }

    enabledTwoFactorMailByUserId(userId: string): Observable<boolean> {
        return this.patch<boolean>(`/mfamail/enable/${userId}`, {});
    }

    enabledTwoFactorByUserId(userId: string): Observable<boolean> {
        return this.patch<boolean>(`/mfa/enable/${userId}`, {});
    }
}
